// ─── Product Types ───────────────────────────────────────────────────────────

export type MatSize = 'Small' | 'Medium' | 'Large' | 'XL' | 'XXL' | '3XL'
export type MatThickness = '3mm' | '4mm' | '6mm'
export type MatMaterial = 'Cloth Standard' | 'Cloth Premium' | 'Hard Surface' | 'RGB Edge'
export type StitchingColor = 'Black' | 'White' | 'Purple' | 'Red' | 'Blue' | 'Gold'

export interface MatDimensions {
  width: number  // mm
  height: number // mm
}

export const MAT_SIZES: Record<MatSize, MatDimensions> = {
  'Small':  { width: 250, height: 300 },
  'Medium': { width: 350, height: 300 },
  'Large':  { width: 450, height: 400 },
  'XL':     { width: 800, height: 400 },
  'XXL':    { width: 900, height: 400 },
  '3XL':    { width: 1200, height: 600 },
}

export interface ProductVariant {
  id: string
  size: MatSize
  thickness: MatThickness
  material: MatMaterial
  stitchingColor: StitchingColor
  price: number
  compareAtPrice?: number
  available: boolean
  printifyVariantId?: string
}

export interface Product {
  id: string
  handle: string
  title: string
  description: string
  collectionId: string
  collectionName: string
  images: ProductImage[]
  variants: ProductVariant[]
  tags: string[]
  isCustomizable: boolean
  isPreset: boolean
  printifyProductId?: string
  createdAt: string
  updatedAt: string
}

export interface ProductImage {
  id: string
  url: string
  altText: string
  width: number
  height: number
}

// ─── Collection Types ─────────────────────────────────────────────────────────

export interface Collection {
  id: string
  handle: string
  title: string
  description: string
  image?: ProductImage
  productCount: number
  sortOrder: number
}

export const COLLECTIONS: Collection[] = [
  { id: 'botanic', handle: 'botanic', title: 'Botanic', description: 'Monstera leaves, wildflower fields, tropical palm prints, watercolor ferns', image: undefined, productCount: 0, sortOrder: 1 },
  { id: 'gaming', handle: 'gaming', title: 'Gaming', description: 'Dark circuit boards, glowing HUD overlays, controller silhouettes, pixel art arenas', image: undefined, productCount: 0, sortOrder: 2 },
  { id: 'astral', handle: 'astral', title: 'Astral', description: 'Nebulae, star maps, moon phases, aurora borealis, galaxy swirls', image: undefined, productCount: 0, sortOrder: 3 },
  { id: 'zen-cozy', handle: 'zen-cozy', title: 'Zen & Cozy', description: 'Warm linen textures, candle & book scenes, muted stone palettes, sand ripples', image: undefined, productCount: 0, sortOrder: 4 },
  { id: 'cyberpunk', handle: 'cyberpunk', title: 'Cyberpunk', description: 'Rainy neon cityscapes, glitch art, hacker terminals, synthwave grids', image: undefined, productCount: 0, sortOrder: 5 },
  { id: 'japonaise', handle: 'japonaise', title: 'Japonaise', description: 'Great Wave ukiyo-e, koi fish, cherry blossom branches, torii gates at dusk', image: undefined, productCount: 0, sortOrder: 6 },
  { id: 'fantasy', handle: 'fantasy', title: 'Fantasy', description: 'Dragon over mountain peaks, enchanted forests, ancient spell books, potion shelves', image: undefined, productCount: 0, sortOrder: 7 },
  { id: 'nature', handle: 'nature', title: 'Nature', description: 'Misty pine forests, waterfall cliffs, wolf silhouettes, autumn leaf canopies', image: undefined, productCount: 0, sortOrder: 8 },
  { id: 'lofi-retro', handle: 'lofi-retro', title: 'Lofi & Retro', description: 'Rainy window café, retro CRT screens, cassette tape art, vintage record stores', image: undefined, productCount: 0, sortOrder: 9 },
  { id: 'dark-academia', handle: 'dark-academia', title: 'Dark Academia', description: 'Library bookshelves, antique maps, quill & inkwell, candlelit stone halls', image: undefined, productCount: 0, sortOrder: 10 },
  { id: 'kawaii-cute', handle: 'kawaii-cute', title: 'Kawaii & Cute', description: 'Boba tea characters, sleepy cats, pastel cloud patterns, chibi animals', image: undefined, productCount: 0, sortOrder: 11 },
  { id: 'space-galaxy', handle: 'space-galaxy', title: 'Space & Galaxy', description: 'Astronaut floating, planet rings, black hole art, Milky Way panoramas', image: undefined, productCount: 0, sortOrder: 12 },
  { id: 'seasonal-holiday', handle: 'seasonal-holiday', title: 'Seasonal & Holiday', description: 'Snowy Christmas village, spooky Halloween graveyard, spring cherry blossom, harvest pumpkins', image: undefined, productCount: 0, sortOrder: 13 },
  { id: 'maps-travel', handle: 'maps-travel', title: 'Maps & Travel', description: 'Vintage world maps, city blueprint overheads, travel stamp collages, topographic contours', image: undefined, productCount: 0, sortOrder: 14 },
  { id: 'motivational-quotes', handle: 'motivational-quotes', title: 'Motivational & Quotes', description: 'Bold sans-serif mantras, handwritten script quotes, minimalist affirmations', image: undefined, productCount: 0, sortOrder: 15 },
  { id: 'bd-comics', handle: 'bd-comics', title: 'BD (Comics)', description: 'Comic book art, graphic novel panels, illustrated characters, bold ink styles', image: undefined, productCount: 0, sortOrder: 16 },
  { id: 'manga', handle: 'manga', title: 'Manga', description: 'Japanese manga art styles, iconic characters, dynamic action scenes, expressive illustrations', image: undefined, productCount: 0, sortOrder: 17 },
  { id: 'pop-art', handle: 'pop-art', title: 'Pop Art', description: 'Bold colors, halftone patterns, iconic pop culture imagery, graphic art styles', image: undefined, productCount: 0, sortOrder: 18 },
  { id: 'mandala', handle: 'mandala', title: 'Mandala', description: 'Intricate geometric patterns, spiritual designs, symmetrical art, meditative visuals', image: undefined, productCount: 0, sortOrder: 19 },
]

// ─── Cart Types ───────────────────────────────────────────────────────────────

export interface CartItem {
  id: string
  productId: string
  variantId: string
  title: string
  image: string
  size: MatSize
  thickness: MatThickness
  material: MatMaterial
  stitchingColor: StitchingColor
  price: number
  quantity: number
  isCustom: boolean
  customDesign?: CustomDesign
}

export interface Cart {
  id: string
  items: CartItem[]
  subtotal: number
  currency: string
  checkoutUrl?: string
}

// ─── Custom Design Types ──────────────────────────────────────────────────────

export interface CustomDesign {
  id: string
  imageUrl: string
  imageFile?: File
  cropData: CropData
  position: Position2D
  scale: number
  rotation: number
  canvasSnapshot?: string
  confirmedAt?: string
}

export interface CropData {
  x: number
  y: number
  width: number
  height: number
}

export interface Position2D {
  x: number
  y: number
}

export interface SavedDesign {
  id: string
  name: string
  customerId: string
  design: CustomDesign
  variant: {
    size: MatSize
    thickness: MatThickness
    material: MatMaterial
    stitchingColor: StitchingColor
  }
  previewUrl: string
  createdAt: string
}

// ─── Customer Types ───────────────────────────────────────────────────────────

export interface Customer {
  id: string
  email: string
  firstName: string
  lastName: string
  savedDesigns: SavedDesign[]
  orders: Order[]
}

// ─── Order Types ──────────────────────────────────────────────────────────────

export interface Order {
  id: string
  orderNumber: string
  status: OrderStatus
  items: CartItem[]
  total: number
  currency: string
  createdAt: string
  fulfillmentStatus?: string
  trackingUrl?: string
  printifyOrderId?: string
}

export type OrderStatus = 'pending' | 'processing' | 'fulfilled' | 'shipped' | 'delivered' | 'cancelled'

// ─── API Response Types ───────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T
  error?: string
  message?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

// ─── Filter Types ─────────────────────────────────────────────────────────────

export interface ProductFilters {
  collection?: string
  size?: MatSize
  minPrice?: number
  maxPrice?: number
  search?: string
  sortBy?: 'price-asc' | 'price-desc' | 'newest' | 'popular'
}
